// @ts-ignore-line
import axiosV1 from "@/libs/axiosV1";
import axios from "@/libs/axios";
import axiosInsights from "@/libs/axiosInsights";
import * as lodash from "lodash";
// @ts-ignore-line
import i18n from "@/libs/i18n";
// @ts-ignore
import router from "@/router";

import { useApps } from "@/composables/useApps";
import { AppInterface, AppUtilsInterface } from "@/interfaces/app.interface";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { BackendAppInterface } from "@/interfaces/backendApp.interface";
import ToastInterface from "vue-toastification/dist/types/src/ts/interface";
import { StartChatParamsInterface } from "@/interfaces/startChatParams.interface";

const endpoints = {
    V1: `${(process.env.VUE_APP_API_GATEWAY ?? "").replace("v1", "V2")}/plugins`,
    V2: "apps",
};

const AppUtils = (app: AppInterface): AppUtilsInterface => {
    const { register } = useApps();

    // @ts-ignore
    let $emit: (event: string, ...args: unknown[]) => void;
    let $toast: ReturnType<typeof ToastInterface>;

    const getStorage = <T = unknown>(name: string, defaultValue: T | null = null): Promise<T | null> => {
        return axios
            .get<T>(`${endpoints.V2}/${app.id}/data`, {
                params: {
                    s: {
                        pluginId: app.id,
                        name,
                    },
                    fields: "value",
                },
            })
            .then((response: AxiosResponse) => JSON.parse(lodash.get(response, "data[0].value", defaultValue)));
    };

    // Plugin Storage is only returned on request, which is directly opposite from Plugin Settings.
    const saveStorage = async (name: string, value: any): Promise<void> => {
        // Check if we need to update or create
        const dataId = await axios
            .get(`${endpoints.V2}/${app.id}/data`, {
                params: {
                    s: {
                        pluginId: app.id,
                        name,
                    },
                    fields: "id",
                },
            })
            .then((response: AxiosResponse) => lodash.get(response, "data[0].id"))
            .catch(() => undefined);

        // Create pluginData object
        // Value is stored as JSON to allow for complex data
        const pluginData = {
            id: dataId,
            pluginId: app.id,
            name,
            value: JSON.stringify(value),
        };

        // Update with a put requests (replaces old dataobject)
        if (dataId) {
            await axios.put(`${endpoints.V2}/${app.id}/data/${dataId}`, pluginData);
            return;
        }

        // Store as new object
        await axios.post(`${endpoints.V2}/${app.id}/data`, pluginData);
    };

    return {
        i18n,
        setEmitAndToast(
            emit: (event: string, ...args: unknown[]) => void,
            toast: ReturnType<typeof ToastInterface>
        ): void {
            $emit = emit;
            $toast = toast;
        },
        appendToMessage(message: string): void {
            if ($emit === undefined) {
                throw new Error("Emit hasn't been set");
            }
            $emit("set-chat-entry-input", { message });
        },
        notify(title: string, type: "success" | "error" | "warning" | "info" = "success"): void {
            if ($toast === undefined) {
                throw new Error("Toast hasn't been set");
            }
            switch (type) {
                case "success":
                    $toast.success(title);
                    return;
                case "error":
                    $toast.error(title);
                    return;
                case "warning":
                    $toast.warning(title);
                    return;
                default:
                    $toast.info(title);
            }
        },
        apiCall<T = unknown>(axiosRequestConfig: AxiosRequestConfig): Promise<T> {
            return axiosV1
                .post<T>(`${endpoints.V1}/proxy`, axiosRequestConfig)
                .then((result: AxiosResponse) => result.data)
                .catch((error: AxiosError) => {
                    throw error.response?.data || error;
                });
        },
        insightsApiCall<T = unknown>(url: string): Promise<T> {
            return axiosInsights
                .get<T>(url)
                .then((result: AxiosResponse) => result.data)
                .catch((error: AxiosError) => {
                    throw error.response?.data || error;
                });
        },
        sendEmail<T = unknown>({ email }: { email: unknown }): Promise<T> {
            return axiosV1
                .post<T>(`${endpoints.V1}/${app.id}/email`, email)
                .then((result: AxiosResponse) => result.data)
                .catch((error: AxiosError) => {
                    throw error.response?.data || error;
                });
        },
        startChat(startChatParams: StartChatParamsInterface) {
            router.replace({
                name: "start-conversation",
                query: startChatParams,
            });
        },
        getSetting<T = unknown>(path: string, defaultValue: T): T {
            return lodash.get(app.settings, path, defaultValue);
        },

        getStorage,
        saveStorage,
        // Get & Save Data are aliases for get & save Storage for backwards compatibility reason
        getData: getStorage,
        saveData: saveStorage,

        // eslint-disable-next-line
        async saveSettings(settings: Record<string, any>) {
            const appSettings = Object.entries(settings).map(([name, value]) => ({
                pluginId: app.id,
                name,
                value,
            }));
            await axios
                .put(`${endpoints.V2}/${app.id}`, {
                    settings: appSettings,
                })
                .then((app: AxiosResponse<BackendAppInterface>) => register(app.data));
        },

        // Scroll container to top
        scrollToTop(): void {
            $emit("scroll-to-top");
        },

        setLocalData(key: string, value: string): void {
            localStorage.setItem(`${app.name}/${key}`, JSON.stringify(value));
        },

        getLocalData(key: string): string | null {
            const item = localStorage.getItem(`${app.name}/${key}`);

            return item ? JSON.parse(item) : null;
        },
    };
};

export default AppUtils;
